import moment from 'moment';

/**
 * Builds `OrdersMetricsInput` from the Metrics tab's own filter state.
 *
 * There is no translation layer here any more. The tab used to borrow the
 * order queues' filter, which collects roughly thirty fields against the
 * sixteen this query accepts, so half of what an admin set was silently
 * dropped and a notice had to explain which half. `MetricsFilter` now collects
 * exactly these fields, so everything set is sent.
 *
 * The one thing worth remembering: metrics has no status dimension. It reports
 * every lifecycle stage at once under `flow`, which is why no status control
 * exists on this tab.
 */

/** Fields the filter panel collects, named as the query names them. */
const FILTER_KEYS = [
  'instrumentKey',
  'assetClass',
  'instrumentId',
  'collectionId',
  'orgId',
  'broker',
  'direction',
  'type',
];

const isSet = value =>
  value !== undefined &&
  value !== null &&
  value !== '' &&
  !(Array.isArray(value) && value.length === 0);

const asDate = value => {
  if (!isSet(value)) return undefined;
  const parsed = moment.isMoment(value) ? value : moment(value);
  return parsed.isValid() ? parsed.format() : undefined;
};

/**
 * `controls` carries what the toolbar owns rather than the panel: the window,
 * which timestamp it applies to, an optional single currency, whether to
 * include derivatives, and how to match a broker.
 */
export const buildMetricsInput = ({
  filterValues = {},
  scopedExchangeId,
  userId,
  controls = {},
}) => {
  const input = {};

  FILTER_KEYS.forEach(key => {
    if (isSet(filterValues[key])) input[key] = filterValues[key];
  });

  // `brokerMatch` only means anything alongside a broker.
  if (isSet(input.broker))
    input.brokerMatch = controls.brokerMatch || 'CURRENT';

  // The exchange comes from the page lens rather than the panel.
  if (isSet(scopedExchangeId)) input.stockExchange = scopedExchangeId;

  if (isSet(userId)) input.userId = userId;

  // One range, and the field it applies to. "Ordered in September" and
  // "settled in September" select different orders.
  const startDate = asDate(controls.startDate);
  const endDate = asDate(controls.endDate);
  if (startDate) input.startDate = startDate;
  if (endDate) input.endDate = endDate;
  if (isSet(controls.dateField)) input.dateField = controls.dateField;

  if (isSet(controls.assetCurrency))
    input.assetCurrency = controls.assetCurrency;
  if (controls.includeDerivatives) input.includeDerivatives = true;

  return input;
};

/** Human labels for the applied-filter chips. */
const CHIP_LABELS = {
  instrumentKey: 'Instrument type',
  assetClass: 'Asset class',
  instrumentId: 'Instrument',
  collectionId: 'Collection',
  orgId: 'Organisation',
  broker: 'Broker',
  direction: 'Direction',
  type: 'Order type',
};

const readable = value =>
  typeof value === 'string' && value === value.toUpperCase()
    ? value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ')
    : String(value);

/**
 * The applied filters, as chips.
 *
 * `labels` lets the caller pass display names it already resolved — an
 * instrument or a broker is stored as an id, and an id is not worth showing.
 */
export const metricsFilterChips = (filterValues = {}, user, labels = {}) => {
  const chips = FILTER_KEYS.filter(key => isSet(filterValues[key])).map(
    key => ({
      key,
      label: CHIP_LABELS[key],
      value: labels[filterValues[key]] ?? readable(filterValues[key]),
    }),
  );

  if (user)
    chips.push({
      key: '__user',
      label: 'User',
      value: typeof user === 'string' ? user : user?.name || 'Selected',
    });

  return chips;
};
